import { useEffect, useImperativeHandle } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  usePaymentDetails,
  useUpdatePaymentDetails,
} from '../../../api/paymentModes/hooks'
import { BankDetailsSchema } from '../utils'

const bankDetailsSchema = BankDetailsSchema()

type Props = {
  onChangeError: () => void
  showPopup: () => void
  setIsSubmitDisabled: (x: boolean) => void
  setIsSubmitLoading: (x: boolean) => void
}

type BankFormDetailsType = z.infer<typeof bankDetailsSchema>

export const useAddBankAccountForm = ({
  props,
  ref,
}: {
  props: Props
  ref: any
}) => {
  const { onChangeError, showPopup, setIsSubmitDisabled, setIsSubmitLoading } =
    props
  const { data: paymentModes } = usePaymentDetails()

  const queryClient = useQueryClient()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<BankFormDetailsType>({
    resolver: zodResolver(bankDetailsSchema),
    mode: 'onBlur',
    defaultValues: {
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      panCard: '',
    },
  })

  const {
    mutate,
    data: responseData,
    isLoading,
    reset,
  } = useUpdatePaymentDetails()

  useEffect(() => {
    if (!isEmpty(paymentModes)) {
      setValue('bankName', paymentModes?.bankName || '')
      setValue('accountNumber', paymentModes?.accountNumber || '')
      setValue('ifscCode', paymentModes?.ifscCode || '')
      setValue('panCard', paymentModes?.panNo || '')
    }
  }, [paymentModes])

  useEffect(() => {
    if (isLoading) {
      setIsSubmitLoading(true)
    } else {
      setIsSubmitLoading(false)
    }
  }, [setIsSubmitLoading, isLoading])

  useEffect(() => {
    if (isDirty && isValid) {
      setIsSubmitDisabled(false)
    } else {
      setIsSubmitDisabled(true)
    }
  }, [setIsSubmitDisabled, isDirty, isValid])

  useEffect(() => {
    if (responseData?.status === true) {
      showPopup()
      reset()
    }
  }, [showPopup, responseData, reset])

  const errorPopup = () => {
    onChangeError()
    showPopup()
  }

  const onSubmit = (data: {
    bankName: string
    accountNumber: string
    ifscCode: string
    panCard: string
  }) => {
    mutate(
      {
        bankAccountNo: data.accountNumber,
        bankIfscCode: data?.ifscCode,
        panNo: data?.panCard,
        bankName: data?.bankName,
      },
      {
        onSuccess: (response) => {
          if (response?.data?.status) {
            showPopup()
            queryClient.invalidateQueries({
              queryKey: ['useMyTeamsPaymentDetails'],
            })
            queryClient.invalidateQueries({
              queryKey: ['fetchMyTeamsTotalEarnings'],
            })
            queryClient.invalidateQueries({
              queryKey: ['fetchMyTeamsEarnings'],
            })
          } else if (response?.error?.code === 3315) {
            errorPopup()
          }
        },
      }
    )
  }

  useImperativeHandle(ref, () => ({
    callWrapperBank() {
      handleSubmit(onSubmit)()
    },
  }))
  return {
    control,
    errors,
    paymentModes,
  }
}
